import { supabase, APP_STATE_KEY } from "./supabase";
import { DEFAULT_REFERENCE_DATA } from "@/types/reference";
import type { AppState } from "@/types/state";
import { listCards, syncCards } from "./cardsRepo";

export const EMPTY_APP_STATE: AppState = {
  cards: [],
  referenceData: DEFAULT_REFERENCE_DATA,
  decks: [],
};

/**
 * Load app state. Cards come from the dedicated `cards` table; everything
 * else (referenceData, decks) still lives in the `app_state` JSON blob.
 *
 * Fallback: if the `cards` table is empty (e.g. fresh dev DB or a load that
 * races with the one-time hydration migration), use whatever cards exist in
 * the legacy blob. Once the cards table is populated, this fallback becomes
 * a no-op.
 */
export async function loadAppState(): Promise<AppState> {
  const [tableCards, blobResult] = await Promise.all([
    listCards(),
    supabase
      .from("app_state")
      .select("value")
      .eq("key", APP_STATE_KEY)
      .maybeSingle(),
  ]);

  let blob: Partial<AppState> = {};
  if (blobResult.error) {
    console.error("[loadAppState] blob fetch", blobResult.error);
  } else if (blobResult.data?.value) {
    try {
      blob =
        typeof blobResult.data.value === "string"
          ? JSON.parse(blobResult.data.value)
          : (blobResult.data.value as Partial<AppState>);
    } catch (e) {
      console.error("[loadAppState] parse error", e);
    }
  }

  // Cards-table wins. Only fall back to blob cards if the table is empty.
  const finalCards =
    tableCards.length > 0 ? tableCards : (blob.cards ?? []);

  return mergeWithDefaults({ ...blob, cards: finalCards });
}

/**
 * Save app state. Cards are synced to the dedicated `cards` table; the rest
 * (referenceData, decks) is written to the `app_state` blob with cards
 * stripped out so the blob stops growing.
 *
 * Note: legacy `cards` array inside the blob is intentionally left alone if
 * it's already there — a future cleanup migration can drop it once we've
 * confirmed the new path is stable in production.
 */
export async function saveAppState(state: AppState): Promise<void> {
  // 1. Sync cards table (upsert all + delete missing).
  await syncCards(state.cards);

  // 2. Write the rest of app state to the blob — cards EXCLUDED.
  const blobPayload = JSON.stringify({
    referenceData: state.referenceData,
    decks: state.decks,
  });
  const { error } = await supabase.from("app_state").upsert(
    {
      key: APP_STATE_KEY,
      value: blobPayload,
      updated_at: new Date().toISOString(),
    },
    { onConflict: "key" },
  );
  if (error) throw error;
}

/** Merge two string lists: keep existing order, drop blacklisted entries,
 *  then append any defaults that aren't already present. Case-insensitive de-dup. */
function mergeStringList(
  existing: string[] | undefined,
  defaults: string[],
  blacklist: string[] = [],
): string[] {
  const blackLc = new Set(blacklist.map((s) => s.toLowerCase()));
  const seen = new Set<string>();
  const out: string[] = [];
  for (const v of existing ?? []) {
    const lc = (v ?? "").toLowerCase();
    if (!lc || blackLc.has(lc) || seen.has(lc)) continue;
    seen.add(lc);
    out.push(v);
  }
  for (const v of defaults) {
    const lc = (v ?? "").toLowerCase();
    if (!lc || seen.has(lc)) continue;
    seen.add(lc);
    out.push(v);
  }
  return out;
}

function mergeWithDefaults(s: Partial<AppState>): AppState {
  const stored = s.referenceData ?? ({} as Partial<AppState["referenceData"]>);
  // Legacy keyword names that have been replaced and should be dropped on load.
  const KEYWORD_BLACKLIST = ["Reflect 2"];
  return {
    cards: s.cards ?? [],
    referenceData: {
      ...DEFAULT_REFERENCE_DATA,
      ...stored,
      keywords: mergeStringList(
        stored.keywords,
        DEFAULT_REFERENCE_DATA.keywords,
        KEYWORD_BLACKLIST,
      ),
      mechanics: mergeStringList(stored.mechanics, DEFAULT_REFERENCE_DATA.mechanics),
    },
    decks: s.decks ?? [],
  };
}
